import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, ArrowLeft, Filter, AlertCircle, FileDown, FileText } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { api, extractError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmtDate } from '../lib/date';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { MONTHS_DE, TYPE_BADGE } from '../components/absences/constants';

const fmtH = (v) => (v === null || v === undefined) ? '—' : `${(Math.round(v * 100) / 100).toString().replace('.', ',')}`;
const fmtD = (v) => (v === null || v === undefined) ? '—' : (Number.isInteger(v) ? String(v) : v.toFixed(1).replace('.', ','));

export default function AttendancePage() {
  const { can, isAdmin } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [kstFilter, setKstFilter] = useState('all');
  const [costCenters, setCostCenters] = useState([]);
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // employee_id for detail

  const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
  const kstById = useMemo(() => Object.fromEntries(costCenters.map(k => [k.id, k])), [costCenters]);

  useEffect(() => { api.get('/cost-centers').then(r => setCostCenters(r.data)).catch(() => {}); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/attendance/timesheets', { params: { month: monthStr, cost_center_id: kstFilter } });
      setData(r.data);
    } catch (err) { toast.error(extractError(err)); } finally { setLoading(false); }
  };
  useEffect(() => { if (!selected) load(); /* eslint-disable-next-line */ }, [monthStr, kstFilter, selected]);

  const goPrev = () => { const m = month - 1; if (m < 0) { setMonth(11); setYear(year - 1); } else setMonth(m); };
  const goNext = () => { const m = month + 1; if (m > 11) { setMonth(0); setYear(year + 1); } else setMonth(m); };

  const COLS = [
    ['Name', it => it.employee_name],
    ['Kostenstelle', it => kstById[it.cost_center_id]?.name || ''],
    ['Zu vergütende Tage', it => fmtD(it.payable_days)],
    ['Erfasste Stunden', it => fmtH(it.logged_hours)],
    ['Mögliche Stunden', it => fmtH(it.possible_hours)],
    ['Reguläre Stunden', it => fmtH(it.regular_hours)],
    ['Überstunden', it => fmtH(it.overtime_hours)],
    ['Anwesende Tage', it => String(it.present_days)],
    ['Geleistete Arbeitsstunden', it => fmtH(it.logged_hours)],
    ['Ges. bez. Abwesenheit', it => fmtH(it.paid_absence_hours)],
    ['Tage ohne Zeitdaten', it => String(it.days_without_time_data)],
  ];
  const monthLabel = `${MONTHS_DE[month]} ${year}`;

  const exportCsv = () => {
    const sep = ';';
    const header = COLS.map(c => c[0]).join(sep);
    const rows = data.items.map(it => COLS.map(c => `"${String(c[1](it)).replace(/"/g, '""')}"`).join(sep));
    const csv = '\uFEFF' + [header, ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Stundenzettel_${monthStr}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13);
    doc.text('Stundenzettel – Friedrichs Dach', 14, 12);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
    doc.text(`Monat: ${monthLabel}`, 14, 17);
    autoTable(doc, {
      head: [COLS.map(c => c[0])],
      body: data.items.map(it => COLS.map(c => c[1](it))),
      startY: 22,
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [234, 238, 245], textColor: [15, 23, 42] },
      columnStyles: { 0: { fontStyle: 'bold' } },
      margin: { left: 8, right: 8 },
    });
    doc.save(`Stundenzettel_${monthStr}.pdf`);
  };

  const MonthNav = (
    <div className="flex items-center gap-1">
      <Button size="icon" variant="ghost" onClick={goPrev} data-testid="att-prev"><ChevronLeft size={18} /></Button>
      <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
        <SelectTrigger className="w-36 h-8 text-sm font-semibold" data-testid="att-month"><SelectValue /></SelectTrigger>
        <SelectContent>{MONTHS_DE.map((m, i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
        <SelectTrigger className="w-24 h-8 text-sm font-semibold" data-testid="att-year"><SelectValue /></SelectTrigger>
        <SelectContent>{[year - 2, year - 1, year, year + 1].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
      </Select>
      <Button size="icon" variant="ghost" onClick={goNext} data-testid="att-next"><ChevronRight size={18} /></Button>
    </div>
  );

  if (selected) {
    return <AttendanceDetail employeeId={selected} monthStr={monthStr} year={year} month={month}
      onBack={() => setSelected(null)} monthNav={MonthNav} kstName={kstById[data.items.find(i => i.employee_id === selected)?.cost_center_id]?.name} />;
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div>
        <h1 className="text-xl font-bold font-display text-slate-900">Anwesenheit von Mitarbeitenden</h1>
        <p className="text-sm text-slate-500">Stundenzettel – Stunden ausschließlich aus dem Zeitimport. Keine Pausen. Tage ohne Zeitdaten = „—".</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        {MonthNav}
        <div className="flex items-center gap-2">
          <Select value={kstFilter} onValueChange={setKstFilter}>
            <SelectTrigger className="w-52 h-8 text-sm" data-testid="att-kst"><Filter size={13} className="mr-1" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Kostenstellen</SelectItem>
              {costCenters.filter(k => k.active !== false).map(k => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={!data.items.length} data-testid="att-export-csv"><FileDown size={14} className="mr-1" />CSV</Button>
          <Button size="sm" variant="outline" onClick={exportPdf} disabled={!data.items.length} data-testid="att-export-pdf"><FileText size={14} className="mr-1" />PDF</Button>
        </div>
      </div>

      <div className="border rounded-md overflow-x-auto bg-white">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead>
            <TableHead className="text-right">Zu vergütende Tage</TableHead>
            <TableHead className="text-right">Erfasste Stunden</TableHead>
            <TableHead className="text-right">Mögliche Stunden</TableHead>
            <TableHead className="text-right">Reguläre Stunden</TableHead>
            <TableHead className="text-right">Überstunden</TableHead>
            <TableHead className="text-right">Anwesende Tage</TableHead>
            <TableHead className="text-right">Geleistete Arbeitsstunden</TableHead>
            <TableHead className="text-right">Ges. bez. Abwesenheit</TableHead>
            <TableHead className="text-right">Tage ohne Zeitdaten</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.items.map(it => {
              const kst = kstById[it.cost_center_id];
              return (
                <TableRow key={it.employee_id} className="cursor-pointer" onClick={() => setSelected(it.employee_id)} data-testid={`att-row-${it.employee_id}`}>
                  <TableCell>
                    <div className="font-semibold text-slate-800 hover:text-sky-700">{it.employee_name}</div>
                    <div className="text-[11px] text-slate-500">{kst?.name || '—'}</div>
                  </TableCell>
                  <TableCell className="text-right font-mono">{fmtD(it.payable_days)}</TableCell>
                  <TableCell className="text-right font-mono">{fmtH(it.logged_hours)}</TableCell>
                  <TableCell className="text-right font-mono">{fmtH(it.possible_hours)}</TableCell>
                  <TableCell className="text-right font-mono">{fmtH(it.regular_hours)}</TableCell>
                  <TableCell className="text-right font-mono">{it.overtime_hours > 0 ? <span className="text-amber-700 font-semibold">{fmtH(it.overtime_hours)}</span> : fmtH(it.overtime_hours)}</TableCell>
                  <TableCell className="text-right font-mono">{it.present_days}</TableCell>
                  <TableCell className="text-right font-mono">{fmtH(it.logged_hours)}</TableCell>
                  <TableCell className="text-right font-mono">{fmtH(it.paid_absence_hours)}</TableCell>
                  <TableCell className="text-right font-mono">{it.days_without_time_data > 0 ? <Badge variant="outline" className="bg-slate-100 text-slate-600">{it.days_without_time_data}</Badge> : '0'}</TableCell>
                </TableRow>
              );
            })}
            {!loading && data.items.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-slate-500 py-8">Keine Mitarbeiter.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function isoWeek(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((date - firstThursday) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  return week;
}

function AttendanceDetail({ employeeId, monthStr, year, month, onBack, monthNav, kstName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/attendance/timesheets/${employeeId}`, { params: { month: monthStr } })
      .then(r => setData(r.data))
      .catch(err => toast.error(extractError(err)))
      .finally(() => setLoading(false));
  }, [employeeId, monthStr]);

  const chartData = useMemo(() => {
    if (!data) return [];
    const hpd = data.hours_per_day || 8;
    return data.daily.map(d => ({
      day: d.date.slice(8, 10),
      moeglich: d.possible_hours,
      geleistet: d.logged_hours || 0,
      abwesenheit: d.absence_type ? (d.absence_half_day ? hpd * 0.5 : hpd) : 0,
    }));
  }, [data]);

  const weeks = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    for (const d of data.daily) {
      const dt = new Date(d.date + 'T00:00:00');
      const wk = isoWeek(dt);
      if (!map.has(wk)) map.set(wk, []);
      map.get(wk).push(d);
    }
    return [...map.entries()].map(([wk, days]) => {
      const sumLogged = days.reduce((s, d) => s + (d.logged_hours || 0), 0);
      const sumReg = days.reduce((s, d) => s + d.regular_hours, 0);
      const sumOt = days.reduce((s, d) => s + d.overtime_hours, 0);
      return { wk, days, start: days[0].date, end: days[days.length - 1].date, sumLogged, sumReg, sumOt };
    });
  }, [data]);

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={onBack} data-testid="att-back"><ArrowLeft size={16} className="mr-1" />Zurück</Button>
          <div>
            <h1 className="text-lg font-bold font-display text-slate-900">Anwesenheit von {data?.employee?.name || '…'}</h1>
            <p className="text-xs text-slate-500">{kstName ? `Kostenstelle: ${kstName} · ` : ''}{data?.policy_name || 'Standard'}</p>
          </div>
        </div>
        {monthNav}
      </div>

      {loading && <div className="text-sm text-slate-500 py-10 text-center">Lädt…</div>}

      {data && !loading && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2"><CardTitle className="text-base font-display">Tägliche Aufschlüsselung</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={0} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RTooltip formatter={(v, n) => [`${fmtH(v)} h`, n]} labelFormatter={(l) => `Tag ${l}`} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="geleistet" name="Geleistete Std." stackId="a" fill="#2563eb" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="abwesenheit" name="Abwesenheit" stackId="a" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                    <Line dataKey="moeglich" name="Mögliche Std." stroke="#334155" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base font-display">Aufschlüsselung erfasste Stunden</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <Metric label="Mögliche Arbeitsstunden" value={`${fmtH(data.possible_hours)} h`} />
                <Metric label="Erfasste / Geleistete Stunden" value={`${fmtH(data.logged_hours)} h`} accent />
                <Metric label="Reguläre Arbeitsstunden" value={`${fmtH(data.regular_hours)} h`} />
                <Metric label="Überstunden" value={`${fmtH(data.overtime_hours)} h`} />
                <div className="h-px bg-slate-100 my-1" />
                <Metric label="Zu vergütende Tage" value={fmtD(data.payable_days)} />
                <Metric label="Anwesende Tage" value={data.present_days} />
                <Metric label="Bezahlte Abwesenheit" value={`${fmtD(data.paid_absence_days)} T`} />
                <Metric label="Unbezahlte Abwesenheit" value={`${fmtD(data.unpaid_absence_days)} T`} />
                {data.days_without_time_data > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-1">
                    <AlertCircle size={13} />{data.days_without_time_data} Tage ohne Zeitdaten
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base font-display">Tage nach Woche</CardTitle></CardHeader>
            <CardContent>
              <div className="border rounded-md overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Datum</TableHead>
                    <TableHead>Feiertage / arbeitsfreie Tage</TableHead>
                    <TableHead>Abwesenheit</TableHead>
                    <TableHead className="text-right">Geleistete Arbeitsstunden</TableHead>
                    <TableHead className="text-right">Reguläre Stunden</TableHead>
                    <TableHead className="text-right">Überstunden</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {weeks.map(w => (
                      <React.Fragment key={w.wk}>
                        <TableRow className="bg-slate-50">
                          <TableCell colSpan={3} className="font-semibold text-slate-700">Woche {w.wk}: {fmtDate(w.start)} – {fmtDate(w.end)}</TableCell>
                          <TableCell className="text-right font-mono font-semibold">{fmtH(w.sumLogged)}</TableCell>
                          <TableCell className="text-right font-mono font-semibold">{fmtH(w.sumReg)}</TableCell>
                          <TableCell className="text-right font-mono font-semibold">{fmtH(w.sumOt)}</TableCell>
                        </TableRow>
                        {w.days.map(d => (
                          <TableRow key={d.date} data-testid={`att-day-${d.date}`}>
                            <TableCell className="font-mono text-xs whitespace-nowrap">{d.weekday_label}, {fmtDate(d.date)}</TableCell>
                            <TableCell className="text-xs text-slate-500">{d.free_label || ''}</TableCell>
                            <TableCell>{d.absence_type ? <Badge variant="outline" className={TYPE_BADGE[d.absence_type] || ''}>{d.absence_type}{d.absence_half_day ? ' ½' : ''}</Badge> : ''}</TableCell>
                            <TableCell className="text-right font-mono">{d.logged_hours === null ? <span className="text-slate-400">—</span> : fmtH(d.logged_hours)}</TableCell>
                            <TableCell className="text-right font-mono">{fmtH(d.regular_hours)}</TableCell>
                            <TableCell className="text-right font-mono">{d.overtime_hours > 0 ? <span className="text-amber-700">{fmtH(d.overtime_hours)}</span> : fmtH(d.overtime_hours)}</TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={`font-mono font-semibold ${accent ? 'text-sky-700' : 'text-slate-900'}`}>{value}</span>
    </div>
  );
}
